/**
 * Procedural warrior construction.
 *
 * `buildBody(spec, lod)` returns one merged, skinned BufferGeometry. Every
 * volume is authored in bind-pose world space (metres, Y up, +Z forward) and
 * scaled from the rig, so a 1.75 m grunt and a 1.94 m general come out of the
 * same code with correct proportions.
 *
 * Silhouette priorities, in order (this is what a 60px-tall black shape must
 * still read as):
 *   1. helmet mass + crest / topknot breaking the head outline
 *   2. pauldrons wider than the hips
 *   3. flared tassets and a cape/coat-tail trailing behind
 *   4. tapering limbs with hard boot and bracer edges
 */
import * as THREE from 'three';
import { BONE_INDEX, makeRig, PROPORTIONS } from './rig.js';
import { CharBuilder } from './skin.js';
import { loft, sheet, plate, capShell, extrudeFlat, place, mirrorX, deform, triCount, mergeAll } from './geom.js';

const PI = Math.PI, TAU = PI * 2;
const lerp = (a, b, t) => a + (b - a) * t;
const sstep = (t) => { t = t < 0 ? 0 : t > 1 ? 1 : t; return t * t * (3 - 2 * t); };

/** Level-of-detail dials. LOD3 is handled separately (flat cutout). */
export const LOD_SETTINGS = [
  // LOD0 — hero / close-up. Everything on, geometric lamellar plates.
  {
    seg: 11, head: 11, limb: 7, rings: 99, cape: [11, 9], plates: 7, plateN: 17,
    tassets: 6, tassetRes: [3, 2], extras: 2, ears: true, lames: true, both: true,
    armour: true, trim: true, chunky: false,
  },
  // LOD1 — the readable mid-range body: ~40% of LOD0.
  {
    seg: 7, head: 7, limb: 4, rings: 6, cape: [6, 5], plates: 0, plateN: 0,
    tassets: 4, tassetRes: [2, 1], extras: 0, ears: false, lames: true, both: true,
    armour: true, trim: true, chunky: false,
  },
  // LOD2 — a chunky proxy. Silhouette only: torso, limbs, helmet, pauldrons,
  // one skirt ring, a two-quad cape. Everything else is carried by texture.
  {
    seg: 5, head: 4, limb: 4, rings: 4, cape: [2, 1], plates: 0, plateN: 0,
    tassets: 1, tassetRes: [1, 1], extras: 0, ears: false, lames: false, both: true,
    armour: false, trim: false, chunky: true,
  },
];

/** Decimate a ring list down to at most `n` rings, always keeping the ends. */
function dec(arr, n) {
  if (!n || arr.length <= n) return arr;
  const out = [];
  for (let i = 0; i < n; i++) out.push(arr[Math.round(i * (arr.length - 1) / (n - 1))]);
  return out;
}

/* --------------------------------------------------------------- landmarks */

function landmarks(rig) {
  const W = (n, a) => rig.world[BONE_INDEX[n] * 3 + a];
  return {
    s: rig.scale, bw: rig.build, bulk: rig.bulk, H: rig.height,
    hipY: W('hips', 1), chestY: W('chest', 1), neckY: W('neck', 1), headY: W('head', 1),
    shX: W('upperArmL', 0), shY: W('upperArmL', 1), shZ: W('upperArmL', 2),
    elY: W('foreArmL', 1), wrY: W('handL', 1),
    thX: W('thighL', 0), thY: W('thighL', 1), knY: W('shinL', 1), ankY: W('footL', 1),
  };
}

/* ------------------------------------------------------------------- torso */

// The DW wedge lives here: wide chest, pinched waist, hips that flare again
// under the belt. Shoulder : waist : hem must never be within 10% of each other.
const TORSO_PROFILE = [
  // y,      rx,     rz,    sq,   cz
  [0.868, 0.152, 0.116, 2.5, 0.000],
  [0.945, 0.158, 0.120, 2.6, 0.000],
  [1.018, 0.136, 0.101, 2.6, 0.002],
  [1.098, 0.148, 0.111, 2.7, 0.006],
  [1.188, 0.180, 0.131, 2.8, 0.010],
  [1.268, 0.202, 0.144, 2.8, 0.011],
  [1.348, 0.214, 0.149, 2.8, 0.009],
  [1.418, 0.210, 0.142, 2.7, 0.005],
  [1.478, 0.190, 0.127, 2.6, 0.001],
  [1.528, 0.152, 0.108, 2.4, 0.000],
  [1.562, 0.112, 0.086, 2.2, 0.000],
];

function torsoRadius(y0, k = 0) {
  // interpolate the profile table at a reference-space height
  const P = TORSO_PROFILE;
  if (y0 <= P[0][0]) return P[0];
  if (y0 >= P[P.length - 1][0]) return P[P.length - 1];
  for (let i = 1; i < P.length; i++) {
    if (y0 <= P[i][0]) {
      const t = (y0 - P[i - 1][0]) / (P[i][0] - P[i - 1][0]);
      return [y0, lerp(P[i - 1][1], P[i][1], t) + k, lerp(P[i - 1][2], P[i][2], t) + k,
      lerp(P[i - 1][3], P[i][3], t), lerp(P[i - 1][4], P[i][4], t)];
    }
  }
  return P[P.length - 1];
}

/* ------------------------------------------------------------------- build */

/**
 * @param {object} S  character spec (see variants.js)
 * @param {number} lod 0..2
 * @param {object} uvRects atlas rects
 * @returns {{geometry: THREE.BufferGeometry, rig: object, triangles: number}}
 */
export function buildBody(S, lod, uvRects) {
  const base = LOD_SETTINGS[Math.min(2, Math.max(0, lod | 0))];
  // Named characters get a richer LOD0 — they are the ones the camera gets
  // close to, and the grunt triangle budget is what has to stay tight.
  const L = (lod === 0 && S.tier >= 2)
    ? { ...base, seg: 17, head: 17, limb: 10, plates: 8, plateN: 19, cape: [15, 12] }
    : base;
  const rig = makeRig(S);
  const M = landmarks(rig);
  const B = new CharBuilder(rig, uvRects);
  const s = M.s, bw = M.bw, bulk = M.bulk;
  const Y = (v) => v * s;
  const X = (v) => v * s * bw;
  const Rk = (v) => v * s * bulk;

  const tint = S.tint;

  /* ---- helper: mirrored pair (build left, mirror for right) ---- */
  const pair = (make, optsL, optsR) => {
    const gl = make();
    if (!gl) return;
    const gr = mirrorX(gl);
    B.add(gl, optsL);
    B.add(gr, optsR);
  };
  // upperArm outweighs the clavicle so the deltoid mass swings with the limb
  // instead of staying welded to the ribs when the shoulder rotates.
  const armBones = (sd) => [['upperArm' + sd, 1.3], ['foreArm' + sd, 1.05], ['clavicle' + sd, 0.45], ['hand' + sd, 0.7], ['chest', 0.20]];
  const legBones = (sd) => [['thigh' + sd, 1], ['shin' + sd, 1], ['hips', 0.6], ['foot' + sd, 0.7]];
  const torsoBones = [['chest', 1], ['spine2', 1], ['spine1', 1], ['hips', 0.9], ['neck', 0.5]];

  /* ================================================================ TORSO */
  {
    const rings = dec(TORSO_PROFILE, L.rings).map(([y, rx, rz, sq, cz]) => ({
      y: Y(y), rx: X(rx), rz: Rk(rz), sq, cz: Rk(cz),
    }));
    const g = loft(rings, { seg: L.seg, capTop: true, capBottom: true, vFromY: true });
    B.add(g, {
      tile: S.cuirass === 'cloth' ? 'cloth' : 'lamellar',
      tint: S.cuirass === 'cloth' ? tint.cloth : tint.lam,
      bones: torsoBones,
      shade: (x, y) => 0.92 + 0.18 * sstep((y / Y(1.56)) * 1.1 - 0.05),
    });
  }

  /* --- geometric lamellar rows (LOD0 only): real relief on the cuirass --- */
  if (L.plates > 0 && S.cuirass !== 'cloth' && S.tier > 0) {
    const rows = L.plates, N = L.plateN;
    const y0 = 1.075, y1 = 1.415;
    for (let r = 0; r < rows; r++) {
      const yr = lerp(y0, y1, r / (rows - 1));
      const prof = torsoRadius(yr, 0.004);
      const rx = prof[1], rz = prof[2], cz = prof[4];
      const pw = (TAU * (rx + rz) * 0.5) / N * 1.08;
      for (let k = 0; k < N; k++) {
        const a = -PI / 2 + (k + (r % 2) * 0.5) / N * TAU;
        const ca = Math.cos(a), sa = Math.sin(a);
        const px = rx * ca, pz = rz * sa + cz;
        let nx = ca / rx, nz = sa / rz;
        const nl = Math.hypot(nx, nz); nx /= nl; nz /= nl;
        const th = Math.atan2(nx, nz);
        const g = plate(X(pw), Y(0.080), Rk(0.010), X(rx * 1.05), 0.20);
        place(g, { rot: [-0.10, th, 0], pos: [X(px) + nx * Rk(0.006), Y(yr) + Y(0.008), Rk(pz) + nz * Rk(0.006)] });
        B.add(g, {
          tile: 'lamellar', tint: tint.lam,
          uv: { su: 0.30, sv: 0.22, ou: 0.1 + 0.55 * ((k * 7 + r * 3) % 5) / 5, ov: 0.1 + 0.6 * ((k * 3 + r) % 4) / 4 },
          bones: torsoBones, power: 3,
        });
      }
    }
  }

  /* ------------------------------------------------------- neckline / gorget */
  if (L.trim) {
    const g = loft([
      { y: Y(1.500), rx: X(0.176), rz: Rk(0.120), sq: 2.5 },
      { y: Y(1.540), rx: X(0.152), rz: Rk(0.108), sq: 2.4 },
      { y: Y(1.560), rx: X(0.128), rz: Rk(0.096), sq: 2.3 },
    ], { seg: L.seg, vFromY: true });
    B.add(g, { tile: 'gold', tint: tint.trim, bones: [['chest', 1], ['neck', 0.7], ['spine2', 0.4]] });
  }
  // high collar flaring up behind the neck
  if (S.collar && L.trim) {
    const g = loft([
      { y: Y(1.505), rx: X(0.126), rz: Rk(0.101), sq: 2.2 },
      { y: Y(1.560), rx: X(0.138), rz: Rk(0.114), sq: 2.1, cz: Rk(-0.016) },
      { y: Y(1.610), rx: X(0.143), rz: Rk(0.123), sq: 2.0, cz: Rk(-0.040) },
    ], { seg: L.seg, arc: PI * 0.86, phase: -PI / 2 - PI * 0.43, vFromY: true });
    B.add(g, { tile: 'cape', tint: tint.cape, uv: { su: 0.30, sv: 0.22, ou: 0.05, ov: 0.70 },
      bones: [['neck', 1], ['chest', 0.9], ['head', 0.15]] });
  }

  /* ----------------------------------------------------------------- belt */
  if (L.trim) {
    const g = loft([
      { y: Y(0.972), rx: X(0.154), rz: Rk(0.116), sq: 2.6 },
      { y: Y(1.040), rx: X(0.147), rz: Rk(0.110), sq: 2.6 },
    ], { seg: L.seg, vFromY: true });
    B.add(g, { tile: 'leather', tint: tint.leather, bones: [['hips', 1], ['spine1', 0.8]] });
    const buckle = new THREE.BoxGeometry(X(0.072), Y(0.062), Rk(0.024));
    place(buckle, { pos: [0, Y(1.005), Rk(0.126)] });
    B.add(buckle, { tile: 'gold', tint: tint.trim, bones: [['hips', 1], ['spine1', 0.6]] });
  }

  /* ----------------------------------------------------------------- sash */
  if (S.sash && L.trim) {
    const g = loft([
      { y: Y(0.952), rx: X(0.158), rz: Rk(0.119), sq: 2.5 },
      { y: Y(1.000), rx: X(0.152), rz: Rk(0.115), sq: 2.5 },
      { y: Y(1.052), rx: X(0.150), rz: Rk(0.113), sq: 2.5 },
    ], { seg: L.seg, vFromY: true });
    B.add(g, { tile: 'silk', tint: tint.sash, bones: [['hips', 1], ['spine1', 0.9]] });
    if (L.extras > 0) {
      // knot + two hanging tails on the left hip
      const knot = loft([
        { y: Y(0.985), rx: X(0.030), rz: Rk(0.026), sq: 2 },
        { y: Y(1.012), rx: X(0.048), rz: Rk(0.040), sq: 2 },
        { y: Y(1.040), rx: X(0.026), rz: Rk(0.022), sq: 2 },
      ], { seg: 7, capTop: true, capBottom: true, vFromY: true });
      place(knot, { pos: [X(0.135), 0, Rk(0.075)] });
      B.add(knot, { tile: 'silk', tint: tint.sash, bones: [['hips', 1], ['spine1', 0.6]] });
      for (let t = 0; t < 2; t++) {
        const off = t * 0.055;
        const tail = sheet((u, v, o) => {
          const y = lerp(1.02, 0.60 - t * 0.09, v);
          const w = lerp(0.048, 0.070, v);
          const sway = Math.sin(v * 3.1 + t * 1.7) * 0.035 * v;
          o.set(X(0.135 + off + (u - 0.5) * w + sway * 0.5), Y(y), Rk(0.080 + sway + v * 0.02));
        }, 2, 4, { thickness: Y(0.006) });
        B.add(tail, { tile: 'silk', tint: tint.sash, bones: [['hips', 1], ['spine1', 0.5], ['thighL', 0.3]] });
      }
    }
  }

  /* -------------------------------------------------------------- tassets */
  if (S.tassets > 0 && L.chunky) {
    // one flared skirt ring instead of individual lames
    const prof = torsoRadius(0.96, 0.012);
    const g = loft([
      { y: Y(0.995), rx: X(prof[1]), rz: Rk(prof[2]), sq: 2.6 },
      { y: Y(S.tassetLen), rx: X(prof[1] * 1.42), rz: Rk(prof[2] * 1.42), sq: 2.6 },
    ], { seg: L.seg, vFromY: true });
    B.add(g, { tile: 'lamellar', tint: tint.lam, bones: [['hips', 1.4], ['thighL', 0.4], ['thighR', 0.4], ['spine1', 0.4]], power: 2.2 });
  } else if (S.tassets > 0) {
    const n = Math.min(S.tassets, L.tassets);
    const [nu, nv] = L.tassetRes;
    for (let k = 0; k < n; k++) {
      const a = -PI / 2 + (k + 0.5) / n * TAU;
      const arc = (TAU / n) * 0.86;
      // Per-plate length jitter: a skirt whose hem is one flat ring reads as a
      // cardboard cone. Front plates ride shorter so the legs stay visible.
      const lenJ = 1 - 0.075 * (((k * 5 + 3) % 4) / 3) - (Math.cos(a) > 0.55 ? 0.055 : 0);
      const tipY = lerp(0.985, S.tassetLen, lenJ);
      const g = sheet((u, v, o) => {
        const aa = a + (u - 0.5) * arc;
        const scallop = 1 - 0.19 * Math.cos((u - 0.5) * PI * 2);
        const y = lerp(0.985, tipY, v * scallop);
        const prof = torsoRadius(0.96, 0.012);
        const flare = 1 + v * 0.22 + v * v * 0.20;
        o.set(X(prof[1] * flare * Math.cos(aa)), Y(y), Rk(prof[2] * flare * Math.sin(aa)));
      }, nu, nv, { thickness: Y(0.010), both: L.both });
      const front = Math.cos(a) < 0;
      B.add(g, {
        tile: 'lamellar', tint: tint.lam, uv: { sv: 0.8 },
        bones: [['hips', 1.4], ['thighL', front ? 0.5 : 0.25], ['thighR', front ? 0.5 : 0.25], ['spine1', 0.5]],
        power: 2.2,
        shade: (x, y) => 0.78 + 0.28 * sstep((y - Y(S.tassetLen)) / Y(0.30)),
      });
    }
    // gold hem band
    if (L.extras > 0) {
      const g = loft([
        { y: Y(S.tassetLen + 0.034), rx: X(0.228), rz: Rk(0.177), sq: 2.6 },
        { y: Y(S.tassetLen + 0.004), rx: X(0.234), rz: Rk(0.181), sq: 2.6 },
      ], { seg: L.seg, vFromY: true });
      B.add(g, { tile: 'gold', tint: tint.trim, bones: [['hips', 1.4], ['spine1', 0.4]], power: 2.2 });
    }
  }

  /* ---------------------------------------------------------------- LEGS */
  pair(() => {
    // Baggy campaign trousers: thigh mass, a calf swell and a cuff that blouses
    // over the boot. Two straight sticks is what made the black shape a column.
    const g = loft(dec([
      { y: Y(0.960), rx: Rk(0.118), rz: Rk(0.120), sq: 2.2, cx: X(0.092) },
      { y: Y(0.840), rx: Rk(0.114), rz: Rk(0.116), sq: 2.2, cx: X(0.094) },
      { y: Y(0.680), rx: Rk(0.100), rz: Rk(0.103), sq: 2.2, cx: X(0.093) },
      { y: Y(0.520), rx: Rk(0.086), rz: Rk(0.090), sq: 2.2, cx: X(0.090) },
      { y: Y(0.470), rx: Rk(0.083), rz: Rk(0.088), sq: 2.2, cx: X(0.089) },
      { y: Y(0.380), rx: Rk(0.086), rz: Rk(0.091), sq: 2.2, cx: X(0.088) },
      { y: Y(0.300), rx: Rk(0.082), rz: Rk(0.087), sq: 2.3, cx: X(0.087) },
      { y: Y(0.235), rx: Rk(0.070), rz: Rk(0.074), sq: 2.3, cx: X(0.086) },
      { y: Y(0.150), rx: Rk(0.058), rz: Rk(0.062), sq: 2.2, cx: X(0.085) },
    ], L.chunky ? 3 : L.rings >= 99 ? 9 : 7), { seg: L.limb, capTop: true, vFromY: true });
    return g;
  }, { tile: 'cloth', tint: tint.trouser, bones: legBones('L'), uv: { sv: 1.4 } },
    { tile: 'cloth', tint: tint.trouser, bones: legBones('R'), uv: { sv: 1.4 } });

  /* boots */
  pair(() => loft(dec([
    { y: Y(0.268), rx: Rk(0.068), rz: Rk(0.072), sq: 2.3, cx: X(0.085), cz: 0 },
    { y: Y(0.226), rx: Rk(0.074), rz: Rk(0.079), sq: 2.3, cx: X(0.085), cz: Rk(0.002) },
    { y: Y(0.170), rx: Rk(0.075), rz: Rk(0.082), sq: 2.4, cx: X(0.085), cz: Rk(0.004) },
    { y: Y(0.075), rx: Rk(0.078), rz: Rk(0.092), sq: 2.7, cx: X(0.084), cz: Rk(0.016) },
    { y: Y(0.028), rx: Rk(0.076), rz: Rk(0.108), sq: 3.4, cx: X(0.083), cz: Rk(0.036) },
    { y: Y(0.008), rx: Rk(0.070), rz: Rk(0.100), sq: 3.8, cx: X(0.083), cz: Rk(0.040) },
  ], L.chunky ? 3 : L.rings >= 99 ? 6 : 4), { seg: L.limb, capTop: true, capBottom: !L.chunky, vFromY: true }),
    { tile: 'leather', tint: tint.leather, bones: [['footL', 1.4], ['shinL', 0.8]], power: 2.4 },
    { tile: 'leather', tint: tint.leather, bones: [['footR', 1.4], ['shinR', 0.8]], power: 2.4 });

  /* toe cap + sole */
  if (L.extras > 0) {
    pair(() => {
      const g = loft([
        { y: Y(0.010), rx: Rk(0.066), rz: Rk(0.040), sq: 3.2, cx: X(0.083), cz: Rk(0.120) },
        { y: Y(0.040), rx: Rk(0.058), rz: Rk(0.034), sq: 3.0, cx: X(0.083), cz: Rk(0.140) },
        { y: Y(0.062), rx: Rk(0.040), rz: Rk(0.022), sq: 2.6, cx: X(0.083), cz: Rk(0.150) },
      ], { seg: Math.max(5, L.limb - 2), capTop: true, capBottom: true, vFromY: true });
      return g;
    },
      { tile: 'iron', tint: tint.iron, bones: [['footL', 1.6], ['shinL', 0.4]], power: 2.4 },
      { tile: 'iron', tint: tint.iron, bones: [['footR', 1.6], ['shinR', 0.4]], power: 2.4 });
  }

  /* greaves */
  if (S.greave && L.armour) {
    pair(() => {
      const g = loft([
        { y: Y(0.440), rx: Rk(0.090), rz: Rk(0.094), sq: 2.3, cx: X(0.089) },
        { y: Y(0.340), rx: Rk(0.083), rz: Rk(0.088), sq: 2.3, cx: X(0.088) },
        { y: Y(0.230), rx: Rk(0.073), rz: Rk(0.077), sq: 2.3, cx: X(0.086) },
      ], { seg: Math.max(5, L.limb - 1), arc: PI * 1.15, phase: PI / 2 - PI * 0.575, vFromY: true });
      return g;
    },
      { tile: S.greaveMat || 'steel', tint: tint.steel, bones: [['shinL', 1.5], ['footL', 0.4], ['thighL', 0.3]], power: 2.6 },
      { tile: S.greaveMat || 'steel', tint: tint.steel, bones: [['shinR', 1.5], ['footR', 0.4], ['thighR', 0.3]], power: 2.6 });
    // knee cop
    if (L.extras > 0) {
      pair(() => place(capShell(Rk(0.055), PI * 1.2, PI * 0.55, Math.max(5, L.limb - 1), 3),
        { rot: [PI * 0.44, 0, 0], pos: [X(0.089), Y(0.474), Rk(0.058)] }),
        { tile: 'gold', tint: tint.trim, rigid: 'shinL' },
        { tile: 'gold', tint: tint.trim, rigid: 'shinR' });
    }
  }

  /* ----------------------------------------------------------------- ARMS */
  /* Read this before touching the ring table.
   *
   * The shoulder pivot sits only ~0.18 out from the spine while the cuirass is
   * ~0.21 half-wide, so the humerus axis runs *inside* the ribcage: an arm
   * authored as a plain tube on that axis is swallowed by the torso and only
   * the hand pops out at the belly. Two things keep it out:
   *   - the animation system pulls every hanging shoulder towards ~26 deg of
   *     abduction (`shoulderClearance` in src/anim/clips.js);
   *   - the limb here is authored with a small outboard BOW, so the deltoid /
   *     biceps mass sits proud of the joint and the elbow proud of that, with
   *     the wrist coming back onto the hand bone so the weapon socket and the
   *     modelled hand still agree. The bow is perpendicular to the bone, so it
   *     survives skinning in every pose instead of only the bind one.
   * Keep the bow small: it stacks with the abduction, and past ~0.04 the arms
   * read as held out from the body.
   *
   * `t` runs 0 at the shoulder joint, 1 at the elbow, 2 at the wrist. Every
   * other arm piece (band, bracer, cuff, hand, hanging lame) rides the same
   * centreline so nothing floats off the limb.
   */
  const ARM_BOW = 0.026;
  const armY = (t) => (t <= 1 ? lerp(M.shY, M.elY, t) : lerp(M.elY, M.wrY, t - 1));
  const armCx = (t) => M.shX + X(
    t <= 0 ? 0.022
      : t <= 1 ? lerp(0.022, ARM_BOW, Math.pow(t, 0.68))
        : lerp(ARM_BOW, 0.004, sstep(t - 1)));
  /** one cross-section on the arm centreline */
  const armRing = (t, rx, rz, sq = 2.15) => ({ y: armY(t), rx: Rk(rx), rz: Rk(rz), sq, cx: armCx(t) });

  pair(() => loft(dec([
    // acromion -> deltoid crown -> biceps -> elbow knob -> forearm belly -> wrist
    armRing(-0.185, 0.060, 0.062),
    armRing(-0.050, 0.094, 0.091),
    armRing(0.090, 0.100, 0.095),
    armRing(0.300, 0.090, 0.088),
    armRing(0.580, 0.078, 0.078),
    armRing(0.860, 0.066, 0.067),
    armRing(1.030, 0.069, 0.071, 2.30),
    armRing(1.440, 0.059, 0.061, 2.25),
    armRing(1.970, 0.044, 0.046, 2.20),
  ], L.chunky ? 4 : L.rings >= 99 ? 9 : 6), {
    seg: L.limb, capTop: true, capBottom: S.pauldron === 'none', vFromY: true,
  }),
    { tile: S.sleeve || 'cloth', tint: tint.sleeve, bones: armBones('L'), uv: { sv: 1.3 },
      shade: (x, y) => 0.90 + 0.20 * sstep((y - armY(1.6)) / Y(0.42)) },
    { tile: S.sleeve || 'cloth', tint: tint.sleeve, bones: armBones('R'), uv: { sv: 1.3 },
      shade: (x, y) => 0.90 + 0.20 * sstep((y - armY(1.6)) / Y(0.42)) });

  /* upper-arm bands — separates the arm from the cuirass at a glance */
  if (S.pauldron !== 'none' && L.armour) {
    pair(() => loft([
      armRing(0.42, 0.090, 0.091, 2.4),
      armRing(0.64, 0.080, 0.081, 2.4),
    ], { seg: L.limb, vFromY: true }),
      { tile: 'gold', tint: tint.trim, bones: [['upperArmL', 1.8], ['foreArmL', 0.3]], power: 2.6 },
      { tile: 'gold', tint: tint.trim, bones: [['upperArmR', 1.8], ['foreArmR', 0.3]], power: 2.6 });
  }

  /* bracers */
  if (S.bracer && L.armour) {
    pair(() => loft([
      armRing(1.08, 0.074, 0.076, 2.45),
      armRing(1.50, 0.064, 0.066, 2.55),
      armRing(1.95, 0.052, 0.054, 2.60),
    ], { seg: L.limb, vFromY: true }),
      { tile: S.bracerMat || 'leather', tint: tint.leather, bones: [['foreArmL', 1.6], ['handL', 0.4], ['upperArmL', 0.3]], power: 2.6 },
      { tile: S.bracerMat || 'leather', tint: tint.leather, bones: [['foreArmR', 1.6], ['handR', 0.4], ['upperArmR', 0.3]], power: 2.6 });
    if (L.extras > 0) {
      pair(() => loft([
        armRing(1.94, 0.056, 0.058, 2.6),
        armRing(2.01, 0.056, 0.058, 2.6),
      ], { seg: L.limb, vFromY: true }),
        { tile: 'gold', tint: tint.trim, rigid: 'foreArmL' },
        { tile: 'gold', tint: tint.trim, rigid: 'foreArmR' });
    }
  }

  /* hands
   * A faceted mitt is the tell that reads in every close-up, so the palm is
   * short and boxy and the digits are separate tubes with a real gap between
   * them, curled forward around a haft. Heroes get four fingers at full
   * resolution; rank-and-file get three cheaper ones — at grunt distance the
   * count is invisible but the *separation* still reads. */
  if (!L.chunky) pair(() => {
    const hx = armCx(2.0);
    const g = loft([
      { y: M.wrY + Y(0.006), rx: Rk(0.046), rz: Rk(0.038), sq: 2.6, cx: hx },
      { y: M.wrY - Y(0.040), rx: Rk(0.053), rz: Rk(0.044), sq: 2.9, cx: hx, cz: Rk(0.006) },
      { y: M.wrY - Y(0.084), rx: Rk(0.050), rz: Rk(0.042), sq: 3.0, cx: hx, cz: Rk(0.010) },
      { y: M.wrY - Y(0.112), rx: Rk(0.037), rz: Rk(0.033), sq: 2.6, cx: hx, cz: Rk(0.008) },
    ], { seg: Math.max(5, L.limb - 2), capTop: true, capBottom: true, vFromY: true });
    if (L.extras > 0) {
      const nF = S.tier >= 2 ? 4 : 3;
      const fseg = S.tier >= 2 ? 5 : 4;
      const thumb = loft([
        { y: M.wrY - Y(0.018), rx: Rk(0.023), rz: Rk(0.022), sq: 2.1, cz: Rk(0.004) },
        { y: M.wrY - Y(0.052), rx: Rk(0.020), rz: Rk(0.019), sq: 2.1, cz: Rk(0.034) },
        { y: M.wrY - Y(0.084), rx: Rk(0.016), rz: Rk(0.015), sq: 2.1, cz: Rk(0.054) },
      ], { seg: fseg, capTop: true });
      place(thumb, { pos: [hx - Rk(0.036), 0, 0] });
      const parts = [g, thumb];
      const pitch = nF === 4 ? 0.0225 : 0.0300;   // knuckle spacing
      const frad = nF === 4 ? 0.0102 : 0.0132;    // finger radius (< pitch/2 => a visible gap)
      for (let f = 0; f < nF; f++) {
        const fx = hx + Rk(0.031 - f * pitch);
        const fy = M.wrY - Y(0.038 + f * 0.005);
        const rr = Rk(frad - f * 0.0007);
        parts.push(loft([
          { y: fy, rx: rr * 0.92, rz: rr * 0.95, sq: 2.3, cx: fx, cz: Rk(0.030) },
          { y: fy - Y(0.032), rx: rr, rz: rr * 1.22, sq: 2.4, cx: fx, cz: Rk(0.062) },
          { y: fy - Y(0.062), rx: rr * 0.94, rz: rr * 1.10, sq: 2.4, cx: fx, cz: Rk(0.064) },
          { y: fy - Y(0.084), rx: rr * 0.74, rz: rr * 0.80, sq: 2.3, cx: fx, cz: Rk(0.040) },
        ], { seg: fseg, capTop: true, vFromY: false }));
      }
      return mergeAll(parts);
    }
    return g;
  },
    { tile: 'skin', tint: tint.skin, bones: [['handL', 1.8], ['foreArmL', 0.5]], power: 2.6 },
    { tile: 'skin', tint: tint.skin, bones: [['handR', 1.8], ['foreArmR', 0.5]], power: 2.6 });

  /* ------------------------------------------------------------ PAULDRONS */
  if (S.pauldron !== 'none' || S.pauldronL || S.pauldronR) {
    /*
     * Flat/faceted sode survive the gameplay camera better than a many-ring
     * dome: the old surface had a hard-coded 1.36pi round arc and produced the
     * same salmon/blue saucer on every unit. These are authored X/Y outlines
     * with real Z thickness, so their corners remain visible as a black shape.
     * Every layer is still merged into the body buffer.
     */
    const SHAPES = {
      square: {
        depth: 0.142,
        shell: [[0.102, 1.596], [0.174, 1.640], [0.276, 1.596], [0.292, 1.520], [0.276, 1.404], [0.190, 1.360], [0.112, 1.426]],
        band: [[0.128, 1.538], [0.270, 1.512], [0.276, 1.456], [0.142, 1.474]],
        lip: [[0.112, 1.426], [0.190, 1.360], [0.276, 1.404], [0.266, 1.438], [0.190, 1.394], [0.122, 1.454]],
      },
      shielded: {
        depth: 0.158,
        shell: [[0.102, 1.598], [0.182, 1.650], [0.304, 1.604], [0.326, 1.532], [0.310, 1.414], [0.252, 1.344], [0.124, 1.410]],
        band: [[0.136, 1.548], [0.298, 1.520], [0.302, 1.458], [0.154, 1.474]],
        lip: [[0.124, 1.410], [0.252, 1.344], [0.310, 1.414], [0.296, 1.448], [0.248, 1.382], [0.136, 1.442]],
      },
      stepped: {
        depth: 0.154,
        shell: [[0.100, 1.600], [0.174, 1.656], [0.278, 1.626], [0.320, 1.566], [0.304, 1.500], [0.334, 1.446], [0.286, 1.366], [0.176, 1.346], [0.112, 1.426]],
        band: [[0.132, 1.550], [0.286, 1.530], [0.306, 1.476], [0.154, 1.474]],
        lip: [[0.112, 1.426], [0.176, 1.346], [0.286, 1.366], [0.334, 1.446], [0.306, 1.464], [0.278, 1.400], [0.180, 1.382], [0.124, 1.454]],
      },
      swept: {
        depth: 0.144,
        shell: [[0.104, 1.592], [0.176, 1.644], [0.296, 1.668], [0.350, 1.626], [0.326, 1.546], [0.348, 1.474], [0.284, 1.370], [0.168, 1.354], [0.112, 1.428]],
        band: [[0.136, 1.548], [0.314, 1.570], [0.326, 1.506], [0.154, 1.474]],
        lip: [[0.112, 1.428], [0.168, 1.354], [0.284, 1.370], [0.348, 1.474], [0.318, 1.490], [0.274, 1.408], [0.174, 1.390], [0.124, 1.454]],
      },
      banner: {
        depth: 0.116,
        shell: [[0.104, 1.596], [0.166, 1.626], [0.238, 1.592], [0.246, 1.506], [0.214, 1.414], [0.126, 1.438]],
        band: [[0.126, 1.548], [0.230, 1.528], [0.226, 1.476], [0.136, 1.486]],
        lip: [[0.126, 1.438], [0.214, 1.414], [0.246, 1.506], [0.230, 1.514], [0.204, 1.446], [0.132, 1.464]],
      },
      heroWing: {
        depth: 0.170,
        shell: [[0.098, 1.598], [0.182, 1.672], [0.314, 1.654], [0.388, 1.602], [0.402, 1.512], [0.360, 1.418], [0.320, 1.332], [0.196, 1.310], [0.110, 1.408]],
        band: [[0.130, 1.554], [0.344, 1.568], [0.370, 1.506], [0.156, 1.474]],
        lip: [[0.110, 1.408], [0.196, 1.310], [0.320, 1.332], [0.360, 1.418], [0.334, 1.442], [0.304, 1.370], [0.202, 1.350], [0.124, 1.438]],
      },
      heroCompact: {
        depth: 0.130,
        shell: [[0.104, 1.596], [0.166, 1.640], [0.246, 1.600], [0.258, 1.532], [0.238, 1.432], [0.170, 1.388], [0.116, 1.438]],
        band: [[0.132, 1.548], [0.238, 1.526], [0.240, 1.478], [0.142, 1.486]],
        lip: [[0.116, 1.438], [0.170, 1.388], [0.238, 1.432], [0.258, 1.532], [0.240, 1.536], [0.222, 1.460], [0.174, 1.422], [0.126, 1.462]],
      },
    };
    const paulBones = (sd) => [['clavicle' + sd, 4.0], ['chest', 0.8], ['upperArm' + sd, 0.22]];
    const sideKind = (sd) => S['pauldron' + sd] ?? S.pauldron;
    const shapeFor = (type) => {
      if (S.pauldronProfile === 'hero') return type === 'winged' ? SHAPES.heroWing : SHAPES.heroCompact;
      return SHAPES[S.pauldronProfile] || SHAPES.square;
    };
    const addSide = (sd) => {
      const type = sideKind(sd);
      if (!type || type === 'none') return;
      const P = shapeFor(type);
      const size = S.pauldronProfile === 'hero' ? 1 : type === 'winged' ? 1.08 : type === 'large' ? 1.0 : 0.84;
      const scalePts = (pts) => pts.map(([x, y]) => [
        X(0.098 + (x - 0.098) * size),
        Y(1.500 + (y - 1.500) * (0.92 + size * 0.08)),
      ]);
      const orient = (g) => {
        if (sd === 'R') g = mirrorX(g);
        place(g, { pos: [0, 0, M.shZ] });
        return g;
      };
      B.add(orient(extrudeFlat(scalePts(P.shell), Rk(P.depth))), {
        tile: 'lamellar', tint: tint.lamHi, bones: paulBones(sd), power: 2.4,
        uv: { su: 0.74, sv: 0.66 },
        shade: (x, y) => 0.84 + 0.30 * sstep((y - Y(1.35)) / Y(0.30)),
      });
      // One broad face band reads as layered armour; the old stack of narrow
      // rings turned into scalloped texture noise at gameplay distance.
      if (L.plates > 0) B.add(orient(extrudeFlat(scalePts(P.band), Rk(P.depth + 0.018))), {
        tile: 'lamellar', tint: tint.lam, bones: paulBones(sd), power: 2.4,
        uv: { su: 0.58, sv: 0.24, ov: 0.22 },
      });
      if (L.trim) B.add(orient(extrudeFlat(scalePts(P.lip), Rk(P.depth + 0.026))), {
        tile: 'gold', tint: tint.trim, bones: paulBones(sd), power: 2.4,
      });
    };
    addSide('L');
    addSide('R');
  }

  /* -------------------------------------------------------- chest straps */
  if (L.extras > 0 && S.straps) {
    for (const sd of [1, -1]) {
      const g = sheet((u, v, o) => {
        const t = v;
        const x = lerp(sd * 0.10, -sd * 0.075, t);
        const y = lerp(1.455, 1.045, t);
        const prof = torsoRadius(y, 0.010);
        const z = prof[2] * Math.sqrt(Math.max(0, 1 - Math.pow(Math.abs(x) / prof[1], 2.4))) + prof[4];
        o.set(X(x + (u - 0.5) * 0.052), Y(y), Rk(z));
      }, 1, 4, { thickness: Y(0.006) });
      B.add(g, { tile: 'leather', tint: tint.leather, bones: torsoBones });
    }
  }

  /* ------------------------------------------------------------ NECK/HEAD */
  if (!L.chunky) {
    // A warrior's neck is a wedge of trapezius, not a straw: the old 0.074
    // column left the head visibly floating above the gorget.
    const g = loft([
      { y: Y(1.470), rx: Rk(0.104), rz: Rk(0.092), sq: 2.2 },
      { y: Y(1.512), rx: Rk(0.090), rz: Rk(0.080), sq: 2.1 },
      { y: Y(1.548), rx: Rk(0.080), rz: Rk(0.074), sq: 2 },
      { y: Y(1.586), rx: Rk(0.076), rz: Rk(0.072), sq: 2, cz: Rk(0.004) },
    ], { seg: Math.max(6, L.limb), vFromY: true });
    B.add(g, { tile: 'skin', tint: tint.skin, bones: [['neck', 1.6], ['head', 0.6], ['chest', 0.5]], power: 2.6 });
  }
  {
    const HS = 1.22;
    const HEAD = [
      [1.560, 0.055, 0.061, 0.012],
      [1.598, 0.077, 0.086, 0.014],
      [1.631, 0.088, 0.096, 0.011],
      [1.664, 0.096, 0.102, 0.007],
      [1.693, 0.100, 0.104, 0.002],
      [1.721, 0.101, 0.102, 0.000],
      [1.749, 0.097, 0.096, -0.002],
      [1.776, 0.079, 0.077, -0.004],
      [1.800, 0.038, 0.036, -0.005],
    ];
    const g = loft(dec(HEAD, L.chunky ? 4 : L.rings >= 99 ? 8 : 7).map(([y, rx, rz, cz]) => ({ y: Y(y), rx: Rk(rx * HS), rz: Rk(rz * HS), cz: Rk(cz), sq: 2.1 })),
      {
        seg: L.head, capTop: true, vFromY: true,
        uMap: (t) => { const d = t - 0.5, m = Math.abs(d) * 2; return 0.5 + Math.sign(d) * 0.5 * Math.pow(m, 0.55); },
      });
    // Facial structure. The face used to be a painted decal on an almost flat
    // plane; every landmark a light can catch is modelled here instead —
    // brow ridge, eye sockets, cheekbones, a tapered jaw and a chin.
    deform(g, (x, y, z, out) => {
      const yr = y / s;
      const ax = Math.abs(x) / s;
      let dz = 0;
      dz += 0.020 * sstep(1 - Math.abs(yr - 1.708) / 0.030) * sstep(1 - ax / 0.100);  // brow ridge
      dz -= 0.011 * sstep(1 - Math.abs(yr - 1.690) / 0.020) * sstep(1 - Math.abs(ax - 0.055) / 0.040); // eye socket
      dz += 0.016 * sstep(1 - Math.abs(yr - 1.668) / 0.030) * sstep(1 - Math.abs(ax - 0.082) / 0.042); // cheekbone
      dz += 0.014 * sstep(1 - Math.abs(yr - 1.696) / 0.046) * sstep(1 - ax / 0.040);  // nose bridge
      dz += 0.007 * sstep(1 - Math.abs(yr - 1.634) / 0.020) * sstep(1 - ax / 0.052);  // mouth
      dz += 0.019 * sstep(1 - Math.abs(yr - 1.596) / 0.034) * sstep(1 - ax / 0.048);  // chin
      // jaw taper: a cylinder head is what makes a face read as a ball
      const jaw = 1 - 0.16 * sstep((1.660 - yr) / 0.080);
      out.x = x * jaw;
      if (z > 0) out.z = z * (0.90 + 0.10 * jaw) + dz * s;
      if (z < -0.03 * s) out.z = z * 0.95;
    });
    B.add(g, { tile: 'face', tint: tint.skin, bones: [['head', 2.0], ['neck', 0.35]], power: 2.6 });
  }
  // modelled nose — a real wedge, so the profile is not a painted smear
  if (L.extras > 0) {
    const nose = loft([
      { y: Y(1.714), rx: Rk(0.014), rz: Rk(0.017), sq: 2.2, cz: Rk(0.108) },
      { y: Y(1.697), rx: Rk(0.016), rz: Rk(0.021), sq: 2.3, cz: Rk(0.114) },
      { y: Y(1.680), rx: Rk(0.020), rz: Rk(0.025), sq: 2.5, cz: Rk(0.120) },
      { y: Y(1.668), rx: Rk(0.024), rz: Rk(0.026), sq: 2.6, cz: Rk(0.118) },
      { y: Y(1.658), rx: Rk(0.025), rz: Rk(0.021), sq: 2.6, cz: Rk(0.110) },
    ], { seg: 7, capTop: true, capBottom: true, vFromY: false });
    B.add(nose, { tile: 'skin', tint: tint.skin, rigid: 'head' });
  }
  if (L.ears && (S.helmet === 'topknot' || S.helmet === 'none')) {
    pair(() => {
      const g = capShell(Rk(0.030), PI * 2, PI, 6, 4);
      g.scale(0.5, 1.5, 1.0);
      place(g, { rot: [0, 0.25, 0], pos: [Rk(0.112), Y(1.685), Rk(0.000)] });
      return g;
    },
      { tile: 'skin', tint: tint.skin, rigid: 'head' },
      { tile: 'skin', tint: tint.skin, rigid: 'head' });
  }
  // beard / moustache
  if (S.beard > 0 && L.extras > 0) {
    const g = sheet((u, v, o) => {
      const a = (u - 0.5) * PI * 0.62;
      const y = lerp(1.632, 1.632 - (S.beard === 2 ? 0.19 : 0.064), v);
      const r = lerp(0.108, 0.088, v) * (1 - 0.25 * v * v);
      o.set(Rk(Math.sin(a) * r), Y(y), Rk(Math.cos(a) * r * 1.06 + 0.006));
    }, 4, S.beard === 2 ? 3 : 1, { thickness: Y(0.008) });
    B.add(g, { tile: 'hair', tint: tint.hair, rigid: 'head' });
  }

  /* --------------------------------------------------------------- HELMET */
  buildHeadgear(B, S, L, M, { Y, X, Rk, s, tint });

  /* ---------------------------------------------------------------- CAPE */
  if (S.cape > 0) {
    const [nu, nv] = L.cape;
    const bot = S.cape === 2 ? 0.46 : 0.78;
    /* Real cloth, not a kite. Three things do the work:
     *   1. GATHERS — the sheet carries more cloth than the arc it hangs from, so
     *      it buckles into ~4 vertical folds whose depth grows toward the hem.
     *   2. A BROKEN HEM — the hem rides the fold phase (a fold trough hangs
     *      lower than a crest) so the bottom edge is a wave, never a straight cut.
     *   3. ASYMMETRY — the whole drape is swept to one side and the far edge
     *      curls forward around the ribs, which kills the flat-trapezoid read.
     */
    const FOLDS = 3.5;
    const g = sheet((u, v, o) => {
      const t = (u - 0.5) * 2;
      const ph = u * PI * 2 * FOLDS + 0.55;
      const gth = Math.sin(ph);
      const amp = 0.014 + 0.100 * v * v;              // fold depth grows downward
      const cw = Math.cos(t * PI * 0.5);              // 1 at the spine, 0 at the edges
      // width: narrow across the shoulders, flaring to a wide sweep at the hem
      const halfW = lerp(0.145, 0.352, v * 0.32 + v * v * 0.68) * (1 + 0.055 * Math.cos(ph));
      const zBack = lerp(-0.128, -0.300, v * v * 0.62 + v * 0.38);
      const wrap = (1 - cw) * 0.086 * (1 - 0.42 * v);  // edges curl around the ribs
      const sweep = (S.capeSweep ?? 0.052) * v * v;    // trailing to the left
      let y = lerp(1.548, bot, v)
        - (1 - cw) * 0.052                             // cloth sags between the clasps
        + Math.cos(ph * 0.5 + 0.4) * 0.062 * v * v     // wavy hem
        - 0.030 * v * v * (1 - Math.abs(t));
      // Named capes end in three readable tails. Two triangular lifts grow only
      // through the bottom third, creating real negative-space notches without
      // adding bones, a second material, or a separate mesh.
      if (S.capeNotch > 0) {
        const nL = Math.max(0, 1 - Math.abs(t + 0.32) / 0.18);
        const nR = Math.max(0, 1 - Math.abs(t - 0.32) / 0.18);
        y += S.capeNotch * sstep((v - 0.68) / 0.32) * Math.max(nL, nR);
      }
      o.set(X(t * halfW + sweep + gth * amp * 0.42), Y(y),
        Rk(zBack + wrap - gth * amp * (0.35 + 0.65 * cw) - v * v * 0.055));
    }, nu, nv, { thickness: Y(0.011), both: L.both });
    B.add(g, {
      tile: 'cape', tint: tint.cape,
      bones: [['chest', 1.4], ['spine2', 1.0], ['spine1', 0.8], ['hips', 0.7], ['neck', 0.4]],
      power: 2.0,
      shade: (x, y, z) => {
        // v from height, then the mean drape depth at that v — the difference
        // is how far this vertex sits inside a fold.
        const vv = Math.min(1, Math.max(0, (1.548 - y / s) / (1.548 - bot)));
        const zRef = lerp(-0.128, -0.300, vv * vv * 0.62 + vv * 0.38) * s * bulk;
        return (0.80 + 0.26 * sstep((y - Y(bot)) / Y(1.0)))
          * (0.80 + 0.34 * sstep((z - zRef) / (Y(0.11)) + 0.5));
      },
    });
    // shoulder clasp
    if (L.extras > 0) {
      pair(() => {
        const g2 = capShell(Rk(0.036), PI * 2, PI * 0.6, 7, 3);
        place(g2, { rot: [0.4, 0, 0], pos: [X(0.126), Y(1.528), Rk(-0.090)] });
        return g2;
      },
        { tile: 'gold', tint: tint.trim, rigid: 'chest' },
        { tile: 'gold', tint: tint.trim, rigid: 'chest' });
    }
  }

  /* ------------------------------------------------------- SHIELD / BANNER */
  if (S.shield && L.armour) {
    const g = loft([
      { y: 0, rx: Rk(0.010), rz: Rk(0.010), sq: 2 },
      { y: 0, rx: Rk(0.175), rz: Rk(0.175), sq: 2.6 },
      { y: Rk(-0.030), rx: Rk(0.170), rz: Rk(0.170), sq: 2.6 },
    ], { seg: L.limb + 1, capTop: false, capBottom: true, vFromY: false });
    place(g, { rot: [PI / 2, 0, 0.25], pos: [M.shX * 1.30, lerp(M.elY, M.wrY, 0.4), Rk(0.075)] });
    B.add(g, { tile: 'iron', tint: tint.iron, bones: [['foreArmL', 1.8], ['handL', 0.6], ['upperArmL', 0.3]], power: 2.4 });
    if (L.extras > 0) {
    const boss = capShell(Rk(0.052), PI * 2, PI * 0.5, 7, 3);
    place(boss, { rot: [PI / 2, 0, 0.25], pos: [M.shX * 1.30 + Rk(0.03), lerp(M.elY, M.wrY, 0.4), Rk(0.085)] });
    B.add(boss, { tile: 'gold', tint: tint.trim, rigid: 'foreArmL' });
    }
  }

  const geometry = B.build();
  return { geometry, rig, triangles: triCount(geometry) };
}

/* --------------------------------------------------------------- headgear */

/**
 * A horsehair plume rising off the crown and sweeping back — the single
 * cheapest way to turn a ball head into a Dynasty Warriors head. Built as 1–3
 * tapering strands so it reads as mass, not as a card.
 * @param baseY reference-space height the plume springs from
 * @param k     size multiplier
 */
function addPlume(B, U, baseY, k, rigidHead, L, colour, tile) {
  const { Y, Rh, tint } = U;
  const tier = U.tier || 0;
  const heroSwept = U.profile === 'heroSwept';
  // The hair tile is near-black lacquered horsehair, so the plume colourway has
  // to be lifted well above 1 to read as dyed hair against the sky.
  const c = (colour || tint.plume).clone().multiplyScalar(tile === 'hair' ? 2.9 : 1);
  const strands = L.extras > 0 ? (heroSwept ? 2 : tier >= 2 ? 3 : 2) : 1;
  const nv = L.extras > 0 ? (tier >= 2 ? 5 : 4) : 2;
  for (let i = 0; i < strands; i++) {
    const t = strands === 1 ? 0 : (i / (strands - 1)) - 0.5;   // -0.5..0.5
    const lean = 1 + t * 0.16;
    const g = sheet((u, v, o) => {
      const a = v * (heroSwept ? 1.34 : 1.42);
      const py = baseY + Math.sin(a) * (heroSwept ? 0.255 : 0.235) * k * lean;
      const pz = -0.020 - (1 - Math.cos(a)) * (heroSwept ? 0.315 : 0.255) * k * lean;
      const wd = (heroSwept ? 0.016 + 0.032 * Math.sin(v * PI) : 0.030 + 0.048 * Math.sin(v * PI))
        * k * (1 - v * v * 0.55);
      const uu = (u - 0.5);
      // The hero pair fans apart as it rises, preserving a bright sky slit
      // between the strands instead of merging into one broad brush/loaf.
      const lateral = heroSwept ? t * (0.096 + v * 0.062) * k : t * 0.075 * k;
      o.set(Rh(uu * wd * 2 + lateral), Y(py), Rh(pz + Math.abs(uu) * 0.030 + t * t * 0.02));
    }, 3, nv, { thickness: Y(0.010) });
    B.add(g, { tile: tile || 'silk', tint: c, ...rigidHead });
  }
}

function buildHeadgear(B, S, L, M, U) {
  const { Y, X, Rk, s, tint } = U;
  const HS = 1.21;                       // headgear follows the enlarged skull
  const Rh = (v) => Rk(v * HS);
  const seg = Math.max(6, L.head - 2);
  const T = S.helmet;
  if (T === 'none') return;

  const rigidHead = { rigid: 'head' };

  if (T === 'topknot') {
    // a full hair mass, a tall bound topknot and a headband with ribbons —
    // the hero shows his face, so the hair has to carry the head silhouette
    const headK = S.headProfile === 'heroSwept' ? 0.90 : 1;
    const cap = loft(dec([
      { y: Y(1.744), rx: Rh(0.110 * headK), rz: Rh(0.112 * headK), sq: 2.1, cz: Rh(-0.008) },
      { y: Y(1.764), rx: Rh(0.109 * headK), rz: Rh(0.109 * headK), sq: 2.1, cz: Rh(-0.005) },
      { y: Y(1.784), rx: Rh(0.100 * headK), rz: Rh(0.099 * headK), sq: 2.1 },
      { y: Y(1.800), rx: Rh(0.084 * headK), rz: Rh(0.083 * headK), sq: 2.1 },
      { y: Y(1.812), rx: Rh(0.048 * headK), rz: Rh(0.047 * headK), sq: 2.1 },
    ], L.chunky ? 3 : 5), { seg, capTop: true, vFromY: true });
    B.add(cap, { tile: 'hair', tint: tint.hair, ...rigidHead });
    // hair falling behind the ears and down the nape — mass on the back of the
    // head so the profile is not a bare ball on a neck
    if (!L.chunky) {
    const nape = sheet((u, v, o) => {
      const a = (u - 0.5) * PI * 1.04 - PI / 2;      // centred on the BACK of the head
      const y = lerp(1.748, 1.596 - 0.045 * Math.cos((u - 0.5) * PI * 2), v);
      const r = lerp(0.112, 0.102, v) * (1 + 0.05 * Math.sin(u * PI * 5));
      o.set(Rh(Math.cos(a) * r), Y(y), Rh(Math.sin(a) * r * 1.02 - 0.004 - v * 0.010));
    }, L.extras > 0 ? 6 : 4, L.extras > 0 ? 3 : 2, { thickness: Y(0.010) });
    B.add(nape, { tile: 'hair', tint: tint.hair, ...rigidHead });
    }
    const knot = loft(dec([
      { y: Y(1.802), rx: Rh(0.050), rz: Rh(0.048), sq: 2, cz: Rh(-0.018) },
      { y: Y(1.846), rx: Rh(0.048), rz: Rh(0.046), sq: 2, cz: Rh(-0.052) },
      { y: Y(1.884), rx: Rh(0.034), rz: Rh(0.033), sq: 2, cz: Rh(-0.100) },
      { y: Y(1.902), rx: Rh(0.012), rz: Rh(0.012), sq: 2, cz: Rh(-0.150) },
    ], L.chunky ? 3 : 4), { seg: 6, capTop: true, capBottom: !L.chunky, vFromY: false });
    B.add(knot, { tile: 'hair', tint: tint.hair, ...rigidHead });
    /* The crest. A hero's head must break the outline the way a helmet crest
     * does — this is a gold guan crown with a plume sweeping up and back. */
    if (S.plume && S.tier >= 2) {
      const crown = loft([
        { y: Y(1.756), rx: Rh(0.060), rz: Rh(0.076), sq: 2.3, cz: Rh(-0.010) },
        { y: Y(1.806), rx: Rh(0.052), rz: Rh(0.070), sq: 2.3, cz: Rh(-0.016) },
        { y: Y(1.842), rx: Rh(0.032), rz: Rh(0.050), sq: 2.3, cz: Rh(-0.024) },
      ], { seg: 8, capTop: true, vFromY: true });
      B.add(crown, { tile: 'gold', tint: tint.trim, ...rigidHead });
      const fin = extrudeFlat([
        [-0.09, 0.00], [-0.045, 0.11], [0.015, 0.205], [0.085, 0.185], [0.12, 0.145],
        [0.075, 0.105], [0.025, 0.055], [0.00, 0.005], [0.025, -0.025], [-0.065, -0.030],
      ].map(([a, b]) => [a * s * 0.78, b * s * 0.78]), Rh(0.012));
      place(fin, { rot: [0, PI / 2, 0], pos: [0, Y(1.826), Rh(-0.014)] });
      B.add(fin, { tile: 'gold', tint: tint.trim, ...rigidHead });
      addPlume(B, { Y, Rh, tint, tier: S.tier, profile: S.headProfile }, 1.836, 1.08, rigidHead, L, tint.plume, 'hair');
    }
    if (!L.chunky) {
    const tie = loft([
      { y: Y(1.804), rx: Rh(0.049), rz: Rh(0.047), sq: 2, cz: Rh(-0.024) },
      { y: Y(1.824), rx: Rh(0.047), rz: Rh(0.045), sq: 2, cz: Rh(-0.042) },
    ], { seg: 6, vFromY: false });
    B.add(tie, { tile: 'silk', tint: tint.sash, ...rigidHead });
    const band = loft([
      { y: Y(1.718), rx: Rh(0.107), rz: Rh(0.110), sq: 2.1 },
      { y: Y(1.740), rx: Rh(0.108), rz: Rh(0.110), sq: 2.1 },
    ], { seg, vFromY: true });
    B.add(band, { tile: 'silk', tint: tint.sash, ...rigidHead });
    }
    if (L.extras > 0) {
      for (const sd of [1, -1]) {
        const rib = sheet((u, v, o) => {
          const y = lerp(1.734, 1.480, v);
          const sway = Math.sin(v * 3.2 + (sd > 0 ? 0 : 1.4)) * 0.055 * v;
          o.set(Rh(sd * (0.040 + (u - 0.5) * 0.048) + sway * 0.5), Y(y), Rh(-0.100 - v * 0.070 + sway));
        }, 1, 3, { thickness: Y(0.004) });
        B.add(rib, { tile: 'silk', tint: tint.sash, ...rigidHead });
      }
    }
    return;
  }

  if (T === 'wrap') {
    const wrap = loft([
      { y: Y(1.698), rx: Rh(0.101), rz: Rh(0.104), sq: 2.2 },
      { y: Y(1.738), rx: Rh(0.103), rz: Rh(0.105), sq: 2.2 },
      { y: Y(1.774), rx: Rh(0.090), rz: Rh(0.090), sq: 2.2 },
      { y: Y(1.800), rx: Rh(0.050), rz: Rh(0.048), sq: 2.2 },
    ], { seg, capTop: true, vFromY: true });
    B.add(wrap, { tile: 'cloth', tint: tint.cloth, ...rigidHead });
    const tail = sheet((u, v, o) => {
      const y = lerp(1.752, 1.600, v);
      o.set(Rh((u - 0.5) * 0.05 + 0.06 + v * 0.03), Y(y), Rh(-0.085 - v * 0.045));
    }, 1, 2, { thickness: Y(0.005) });
    B.add(tail, { tile: 'cloth', tint: tint.cloth, ...rigidHead });
    return;
  }

  // --- metal helmets: bowl + brim -----------------------------------------
  const tall = T === 'conical' || T === 'lord';
  const topY = tall ? 1.905 : 1.848;
  const bowl = loft(dec([
    { y: Y(1.678), rx: Rh(0.110), rz: Rh(0.113), sq: 2.2 },
    { y: Y(1.722), rx: Rh(0.112), rz: Rh(0.114), sq: 2.2 },
    { y: Y(1.768), rx: Rh(0.105), rz: Rh(0.105), sq: 2.2 },
    { y: Y(lerp(1.772, topY, 0.55)), rx: Rh(tall ? 0.070 : 0.078), rz: Rh(tall ? 0.070 : 0.078), sq: 2.2 },
    { y: Y(topY), rx: Rh(tall ? 0.020 : 0.034), rz: Rh(tall ? 0.020 : 0.034), sq: 2.2 },
  ], L.chunky ? 3 : 5), { seg, capTop: true, vFromY: true });
  B.add(bowl, { tile: S.helmMat || 'iron', tint: tint.iron, ...rigidHead });

  // brim
  const brim = loft(dec([
    { y: Y(1.690), rx: Rh(0.115), rz: Rh(0.118), sq: 2.2 },
    { y: Y(1.670), rx: Rh(0.148), rz: Rh(0.154), sq: 2.3 },
    { y: Y(1.679), rx: Rh(0.154), rz: Rh(0.161), sq: 2.3 },
  ], L.chunky ? 2 : 3), { seg, vFromY: false });
  B.add(brim, { tile: 'gold', tint: tint.trim, ...rigidHead });

  // finial
  if (!L.chunky) {
  const fin = loft([
    { y: Y(topY - 0.004), rx: Rh(0.026), rz: Rh(0.026), sq: 2 },
    { y: Y(topY + 0.022), rx: Rh(0.030), rz: Rh(0.030), sq: 2 },
    { y: Y(topY + 0.046), rx: Rh(0.010), rz: Rh(0.010), sq: 2 },
  ], { seg: 6, capTop: true, vFromY: false });
  B.add(fin, { tile: 'gold', tint: tint.trim, ...rigidHead });
  }

  // neck lames at the back
  if (L.lames) {
    for (let i = 0; i < (T === 'lord' ? 3 : 2); i++) {
      const yy = 1.676 - i * 0.046;
      const rr = 0.120 + i * 0.026;
      const g = loft([
        { y: Y(yy), rx: Rh(rr), rz: Rh(rr * 1.03), sq: 2.2 },
        { y: Y(yy - 0.048), rx: Rh(rr + 0.020), rz: Rh((rr + 0.020) * 1.03), sq: 2.2 },
      ], { seg, arc: PI * 1.12, phase: -PI / 2 - PI * 0.56, vFromY: true });
      B.add(g, { tile: 'lamellar', tint: tint.lam, uv: { sv: 0.25, ov: i * 0.25 }, ...rigidHead });
    }
  }

  // cheek guards
  if (T === 'crested' || T === 'lord' || T === 'horned') {
    for (const sd of [1, -1]) {
      const g = sheet((u, v, o) => {
        const y = lerp(1.694, 1.586, v);
        const a = (u - 0.5) * 0.5 + (sd > 0 ? 0.62 : -0.62) * PI * 0.5;
        const r = lerp(0.113, 0.101, v);
        o.set(Rh(Math.cos(a) * r * 1.02), Y(y), Rh(Math.sin(a) * r + 0.010));
      }, 1, 2, { thickness: Y(0.008) });
      B.add(g, { tile: S.helmMat || 'iron', tint: tint.iron, ...rigidHead });
    }
  }

  // crest / plume — the head mass that has to break the outline
  if (T === 'crested' || T === 'lord') {
    const h = T === 'lord' ? 1.30 : 1.02;
    const crest = extrudeFlat([
      [-0.09, 0.00], [-0.05, 0.10], [0.02, 0.17], [0.10, 0.20], [0.16, 0.175],
      [0.11, 0.135], [0.05, 0.085], [0.01, 0.02], [0.03, -0.02], [-0.06, -0.03],
    ].map(([a, b]) => [a * s * h, b * s * h]), Rh(0.018));
    place(crest, { rot: [0, PI / 2, 0], pos: [0, Y(topY - 0.010), Rh(-0.010)] });
    B.add(crest, { tile: 'gold', tint: tint.trim, ...rigidHead });
  }
  if (S.plume) {
    addPlume(B, { Y, Rh, tint, tier: S.tier }, topY - 0.012,
      S.tier >= 3 ? 1.24 : S.tier >= 1 ? 1.02 : 0.72, rigidHead, L, tint.plume, 'hair');
  } else if (T !== 'horned') {
    // even a nameless peon gets a horsehair tuft: at 18 m a bare bowl helmet
    // collapses into a featureless knob on a vertical bar
    const tuft = sheet((u, v, o) => {
      const a = (u - 0.5) * PI * 0.85;
      const y = lerp(topY + 0.052, topY - 0.055, v * v * 0.75 + v * 0.25);
      const r = lerp(0.016, 0.062, v);
      o.set(Rh(Math.sin(a) * r * 0.8), Y(y), Rh(-0.006 - Math.cos(a) * r * 0.5 - v * 0.055));
    }, 2, L.chunky ? 1 : 2, { thickness: Y(0.008) });
    B.add(tuft, { tile: 'hair', tint: tint.plume.clone().multiplyScalar(2.2), ...rigidHead });
  }
  if (T === 'horned' && !L.chunky) {
    for (const sd of [1, -1]) {
      const horn = extrudeFlat([
        [0, 0], [0.07, 0.09], [0.13, 0.26], [0.09, 0.20], [0.02, 0.05], [-0.02, 0.0],
      ].map(([a, b]) => [a * s * sd, b * s]), Rh(0.014));
      place(horn, { rot: [0.15, 0, 0], pos: [Rh(0.062 * sd), Y(1.780), Rh(0.010)] });
      B.add(horn, { tile: 'iron', tint: tint.iron, ...rigidHead });
    }
  }
  // brow plate
  if (T === 'lord' || T === 'crested') {
    const g = loft([
      { y: Y(1.702), rx: Rh(0.115), rz: Rh(0.119), sq: 2.2 },
      { y: Y(1.676), rx: Rh(0.117), rz: Rh(0.121), sq: 2.3 },
    ], { seg, arc: PI * 0.92, phase: PI / 2 - PI * 0.46, vFromY: true });
    B.add(g, { tile: 'gold', tint: tint.trim, ...rigidHead });
  }
}
